import { FC, useState, useEffect, Fragment } from 'react';
import {
  useTranslation,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  Theme,
  GenericLink,
  FAIcon,
  IconStyle,
  useBreakpoint,
  Breakpoints,
  NextgenImage,
  useMasterData,
  LoaderType,
} from '@nextgen-web-framework/all';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { Contact } from '@commerce-myshop-webcomponents/sdk/services/models/Contact';
import { Social } from '@commerce-myshop-webcomponents/sdk/services/models/Social';
import { MyShopStatusEnum } from '@commerce-myshop-webcomponents/sdk';
import { SocialShareLink } from '@commerce-webcomponents/ui';
import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { PhoneNumberDisplay } from '@commerce-profile-webcomponents/ui';
import { Tenant } from '@com.amway.commerce/abomonetization-myshopconfig-v1-prod/Schema';
import { MASTERDATA_PK_SOCIALCHANNEL } from '../../config';
import { ContactTypeEnum, ShopHeaderProps } from './types/propTypes';
import styles from './ShopHeader.module.scss';

const ShopHeader: FC<ShopHeaderProps> = (props: ShopHeaderProps): JSX.Element => {
  const viewPortType = useBreakpoint();
  const [currentHref, setCurrentHref] = useState('');
  const [loading, setLoading] = useState(true);
  const {
    shopName,
    shopDescription,
    bannerUrl,
    profileUrl,
    socialInfo,
    status,
    redirectionUrl,
    shopCode,
    isReceiver,
    contactInfo,
  } = props;
  useEffect(() => {
    if (window?.location?.href && shopCode) {
      setCurrentHref(`${window.location.origin + window.location.pathname.replace('/myshop', '/shop')}/${shopCode}`);
      setLoading(false);
    }
  }, []);

  const generateShareBarData = () =>
    JSON.stringify({
      subjectName: encodeURIComponent(shopName),
      shopName: encodeURIComponent(shopName),
      shopDescription: encodeURIComponent(shopDescription),
      shopCode,
    });

  const t = useTranslation();
  const shareMessage = t('myshop.ShopHeader.share');
  const config = ConfigService.getConfigs();
  const environment = config.NEXT_SSR_BASE_PATH;
  const countryCode = String(config.NEXT_PUBLIC_COUNTRY).toUpperCase() as CountryCode;
  const ASSETS_WEB_BASE_PATH = config.DEFAULT_HOST_URL as string;
  const myShopConfigs = useMasterData('MyShopMasterData') as Tenant[];
  const { socialChannels } = myShopConfigs.find((t) => t.pk === MASTERDATA_PK_SOCIALCHANNEL) as Tenant;
  const socials = socialInfo?.filter(
    (data: Social) => data.url && socialChannels.some((channel) => channel.name === data.socialPlatform),
  );
  const contacts = contactInfo?.filter((data: Contact) => data.value);

  const getContactInfoData = (data: Contact): { icon: IconName; url: string; element: React.ReactNode } => {
    const { contactType, value } = data;
    if (contactType === ContactTypeEnum.PHONE) {
      return {
        icon: 'phone',
        url: `tel:${value}`,
        element: <PhoneNumberDisplay phoneNumber={parsePhoneNumberWithError(value as string, countryCode).number} />,
      };
    }
    return {
      icon: 'envelope',
      url: `mailto:${value}`,
      element: <>{value}</>,
    };
  };

  const isDev = environment === 'global-dev' || environment === 'global-qa';

  const getLoaderType = (url: string) => (!url.startsWith(ASSETS_WEB_BASE_PATH) ? LoaderType.WEBFONTS : undefined);

  return (
    <div className={styles['myshop-shop-header-container']} data-testid={'myshop-shop-header-test-id'}>
      <div className={styles['myshop-shop-header-image-container']}>
        <div className={styles['myshop-shop-header-banner-img']}>
          <NextgenImage
            src={bannerUrl}
            alt="my shop banner"
            isRelativeURL={true}
            loaderType={getLoaderType(bannerUrl)}
          />
        </div>
        <div className={styles['myshop-shop-header-profile-img']}>
          <NextgenImage
            className={styles['myshop-shop-header-profile-image']}
            src={profileUrl}
            alt="my shop profile"
            isRelativeURL={true}
            loaderType={getLoaderType(profileUrl)}
          />
        </div>
      </div>
      <div className={styles['myshop-shop-header-content-container']}>
        <div className={styles['myshop-shop-header-text']}>
          <div className={styles['myshop-shop-header-text-name']}>{shopName}</div>
          <div className={styles['myshop-shop-header-text-desc']}>{shopDescription}</div>
        </div>
        {!isReceiver ? (
          <div
            className={styles['myshop-shop-header-button-container']}
            data-testid={'myshop-shop-header-button-test-id'}
          >
            <div className={styles['myshop-shop-header-cta-section']}>
              <GenericLink href={redirectionUrl}>
                <Button
                  title={t('myshop.ShopHeader.editDetails')}
                  buttonStyle={ButtonStyles.OUTLINE}
                  theme={Theme.BLACK}
                  size={viewPortType == Breakpoints.XS ? ButtonSize.SMALL : ButtonSize.MEDIUM}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  role="button"
                  aria-label="Edit Details"
                  data-testid="myshop-shop-header-edit-btn"
                />
              </GenericLink>
            </div>
            <div className={styles['myshop-shop-header-cta-section']}>
              {!loading && (
                <SocialShareLink
                  buttonProps={{
                    title: shareMessage,
                    disabled: status !== MyShopStatusEnum.Active,
                  }}
                  linkProps={{
                    category: 'myshop',
                    data: generateShareBarData(),
                    fullUrl: currentHref,
                    title: encodeURIComponent(shopName),
                  }}
                  isCustomUrl={isDev}
                  shareDrawer={false}
                  shareTitle={true}
                  shareDescriptionsContent={t('myshop.ShareLink.description')}
                  customClass={'myshop-sharebar-custom'}
                />
              )}
            </div>
          </div>
        ) : null}

        {socials && socials.length > 0 && (
          <div className={styles['myshop-shop-header-social-channel']}>
            {socials.map((data: Social) => (
              <div key={data.socialPlatform} className={styles[`div_${data.socialPlatform}`]}>
                <a href={data.url} target="_blank" rel="noreferrer">
                  <FAIcon
                    iconName={
                      socialChannels.find((m) => m.name.toLowerCase() === data.socialPlatform?.toLowerCase())
                        ?.iconName as IconName
                    }
                    iconStyle={IconStyle.BRANDS}
                    className={styles[`${(data.socialPlatform as string).toLowerCase()}-icon`]}
                  />
                </a>
              </div>
            ))}
          </div>
        )}
        {contacts && contacts.length > 0 && (
          <div className={styles['myshop-shop-header-contact-info']}>
            {contacts.map((data: Contact, index: number) => (
              <Fragment key={data.contactType || index}>
                <div className={styles[`div_${data.contactType}`]}>
                  <a href={getContactInfoData(data).url} target="_blank" rel="noreferrer">
                    <span className={styles['myshop-shop-header-contact-data']}>
                      <FAIcon iconName={getContactInfoData(data).icon} iconStyle={IconStyle.REGULAR} />
                      <span className={styles['myshop-shop-header-contact-value']}>
                        {getContactInfoData(data).element}
                      </span>
                    </span>
                  </a>
                </div>
                {contacts.length > 1 && index % 2 === 0 && (
                  <div className={styles['myshop-shop-header-contact-divider']}></div>
                )}
              </Fragment>
            ))}
          </div>
        )}
      </div>
      <div className={styles['myshop-shop-header-divider']}></div>
    </div>
  );
};

export default ShopHeader;
